<template>
  <div class="compare-view">
    <BreadcrumbBar parentName="Stations" currentName="Compare Stations" backRoute="/" />

    <div class="content">

      <!-- Controls -->
      <div class="controls-bar">
        <div class="station-pickers">
          <div class="picker-group">
            <label>Station A</label>
            <select v-model="stationA" :disabled="loading" class="station-select">
              <option value="">Select station...</option>
              <option v-for="s in stations" :key="s.slug" :value="s.slug" :disabled="s.slug === stationB">
                {{ s.name }}
              </option>
            </select>
          </div>

          <div class="vs-badge">VS</div>

          <div class="picker-group">
            <label>Station B</label>
            <select v-model="stationB" :disabled="loading" class="station-select">
              <option value="">Select station...</option>
              <option v-for="s in stations" :key="s.slug" :value="s.slug" :disabled="s.slug === stationA">
                {{ s.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="controls-right">
          <div class="date-controls">
            <div class="range-tabs">
              <button
                v-for="option in rangeOptions"
                :key="option.days"
                :class="['range-tab', { active: selectedDays === option.days && !useCustomDates }]"
                @click="selectRange(option.days)"
                :disabled="loading"
              >{{ option.label }}</button>
            </div>
            <div class="date-inputs">
              <input type="date" v-model="customFrom" :max="today" :disabled="loading" @change="onCustomDateChange">
              <span class="date-sep">→</span>
              <input type="date" v-model="customTo" :max="today" :disabled="loading" @change="onCustomDateChange">
            </div>
          </div>

          <div class="action-btns">
            <button class="btn-compare" @click="loadComparison" :disabled="!canCompare || loading">
              <AppIcon v-if="loading" name="spinner" spin />
              <template v-else><AppIcon name="chart-bar" />Compare</template>
            </button>
            <button class="btn-export" @click="exportToCsv" :disabled="!comparison || loading" title="Export CSV">
              <AppIcon name="download" />
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="state-panel">
        <AppIcon name="spinner" spin />
        <p>Loading comparison...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="!comparison && !error" class="state-panel">
        <AppIcon name="columns" />
        <p>Select two stations and click Compare</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="state-panel error">
        <AppIcon name="exclamation-circle" />
        <p>{{ error }}</p>
      </div>

      <!-- Results -->
      <template v-else-if="comparison">

        <!-- Summary strip -->
        <div class="summary-strip">
          <div class="summary-station">
            <span class="stn-name a">{{ comparison.stationA.name }}</span>
            <div class="stn-metrics">
              <div class="metric">
                <span class="metric-val">{{ comparison.summary.totalPlaysA.toLocaleString() }}</span>
                <span class="metric-lbl">plays</span>
              </div>
              <div class="metric-divider"></div>
              <div class="metric">
                <span class="metric-val">{{ comparison.summary.uniqueA.toLocaleString() }}</span>
                <span class="metric-lbl">unique</span>
              </div>
            </div>
          </div>

          <div class="summary-overlap">
            <div class="overlap-circle">
              <span class="overlap-pct">{{ overlapPct }}%</span>
              <span class="overlap-lbl">overlap</span>
            </div>
            <div class="overlap-detail">
              <AppIcon name="link" />
              {{ sharedTracks.length }} shared tracks
            </div>
          </div>

          <div class="summary-station">
            <span class="stn-name b">{{ comparison.stationB.name }}</span>
            <div class="stn-metrics">
              <div class="metric">
                <span class="metric-val">{{ comparison.summary.totalPlaysB.toLocaleString() }}</span>
                <span class="metric-lbl">plays</span>
              </div>
              <div class="metric-divider"></div>
              <div class="metric">
                <span class="metric-val">{{ comparison.summary.uniqueB.toLocaleString() }}</span>
                <span class="metric-lbl">unique</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Main grid -->
        <div class="main-grid">

          <!-- Station A column -->
          <div class="station-col">
            <div class="col-header a">
              <span class="col-title">{{ comparison.stationA.name }}</span>
              <span class="col-sub">Top {{ topPlayedA.length }} tracks</span>
            </div>

            <!-- List A -->
            <div class="track-list">
              <div class="track-header">
                <span class="th-rank">#</span>
                <span class="th-title">Track</span>
                <span class="th-count">Plays</span>
              </div>
              <div
                v-for="(item, index) in paginatedA"
                :key="item.raw"
                :class="['track-row', { shared: isShared(item), exclusive: !isShared(item) }]"
              >
                <span class="tr-rank">{{ (pageA - 1) * perPage + index + 1 }}</span>
                <span class="tr-info">
                  <span class="tr-title">{{ item.title || item.raw || 'Unknown' }}</span>
                  <span v-if="item.artist" class="tr-artist">{{ item.artist }}</span>
                </span>
                <span v-if="item.category" class="tr-badge" :style="{ backgroundColor: getCategoryColor(item.category, 'A') }">
                  {{ item.category }}
                </span>
                <span v-if="isShared(item)" class="tr-shared-dot" title="Also on Station B">●</span>
                <span class="tr-count">{{ item.count.toLocaleString() }}</span>
              </div>
              <div v-if="paginatedA.length === 0" class="no-data">No data available</div>
            </div>
            <div v-if="totalPagesA > 1" class="pagination">
              <button class="pg-btn" :disabled="pageA === 1" @click="pageA--"><AppIcon name="chevron-left" /></button>
              <span class="pg-info">{{ pageA }} / {{ totalPagesA }}</span>
              <button class="pg-btn" :disabled="pageA === totalPagesA" @click="pageA++"><AppIcon name="chevron-right" /></button>
            </div>
          </div>

          <!-- Shared tracks column -->
          <div v-if="sharedTracks.length > 0" class="shared-col">
            <div class="col-header shared">
              <span class="col-title">Shared Tracks</span>
              <span class="col-sub">{{ sharedTracks.length }} in common</span>
            </div>
            <div class="track-list">
              <div class="track-header">
                <span class="th-title">Track</span>
                <span class="th-count">A</span>
                <span class="th-count">B</span>
              </div>
              <div
                v-for="item in paginatedShared"
                :key="item.raw"
                class="track-row shared-row"
              >
                <span class="tr-info">
                  <span class="tr-title">{{ item.title || item.raw || 'Unknown' }}</span>
                  <span v-if="item.artist" class="tr-artist">{{ item.artist }}</span>
                </span>
                <span class="tr-count">{{ item.countA.toLocaleString() }}</span>
                <span class="tr-count">{{ item.countB.toLocaleString() }}</span>
              </div>
            </div>
            <div v-if="totalPagesShared > 1" class="pagination">
              <button class="pg-btn" :disabled="pageShared === 1" @click="pageShared--"><AppIcon name="chevron-left" /></button>
              <span class="pg-info">{{ pageShared }} / {{ totalPagesShared }}</span>
              <button class="pg-btn" :disabled="pageShared === totalPagesShared" @click="pageShared++"><AppIcon name="chevron-right" /></button>
            </div>
          </div>

          <!-- Station B column -->
          <div class="station-col">
            <div class="col-header b">
              <span class="col-title">{{ comparison.stationB.name }}</span>
              <span class="col-sub">Top {{ topPlayedB.length }} tracks</span>
            </div>

            <!-- List B -->
            <div class="track-list">
              <div class="track-header">
                <span class="th-rank">#</span>
                <span class="th-title">Track</span>
                <span class="th-count">Plays</span>
              </div>
              <div
                v-for="(item, index) in paginatedB"
                :key="item.raw"
                :class="['track-row', { shared: isShared(item), exclusive: !isShared(item) }]"
              >
                <span class="tr-rank">{{ (pageB - 1) * perPage + index + 1 }}</span>
                <span class="tr-info">
                  <span class="tr-title">{{ item.title || item.raw || 'Unknown' }}</span>
                  <span v-if="item.artist" class="tr-artist">{{ item.artist }}</span>
                </span>
                <span v-if="item.category" class="tr-badge" :style="{ backgroundColor: getCategoryColor(item.category, 'B') }">
                  {{ item.category }}
                </span>
                <span v-if="isShared(item)" class="tr-shared-dot" title="Also on Station A">●</span>
                <span class="tr-count">{{ item.count.toLocaleString() }}</span>
              </div>
              <div v-if="paginatedB.length === 0" class="no-data">No data available</div>
            </div>
            <div v-if="totalPagesB > 1" class="pagination">
              <button class="pg-btn" :disabled="pageB === 1" @click="pageB--"><AppIcon name="chevron-left" /></button>
              <span class="pg-info">{{ pageB }} / {{ totalPagesB }}</span>
              <button class="pg-btn" :disabled="pageB === totalPagesB" @click="pageB++"><AppIcon name="chevron-right" /></button>
            </div>
          </div>

        </div>
      </template>

    </div>
  </div>
</template>

<script>
import { fetchStations, compareStations } from '../services/api'
import BreadcrumbBar from '../components/BreadcrumbBar.vue'
import Chart from 'chart.js/auto'

export default {
  name: 'CompareStationsView',
  components: { BreadcrumbBar },
  data() {
    return {
      stations: [],
      stationA: '',
      stationB: '',
      selectedDays: 7,
      useCustomDates: false,
      customFrom: '',
      customTo: '',
      today: new Date().toISOString().split('T')[0],
      rangeOptions: [
        { days: 1, label: '24H' },
        { days: 7, label: '7D' },
        { days: 14, label: '14D' },
        { days: 30, label: '30D' }
      ],
      loading: false,
      error: null,
      comparison: null,
      perPage: 25,
      pageA: 1,
      pageB: 1,
      pageShared: 1,
      charts: { categoryA: null, categoryB: null }
    }
  },
  computed: {
    canCompare() {
      return this.stationA && this.stationB && this.stationA !== this.stationB
    },
    topPlayedA() {
      return this.comparison?.topPlayedA || []
    },
    topPlayedB() {
      return this.comparison?.topPlayedB || []
    },
    sharedRaws() {
      const raws = new Set()

      if (this.comparison?.commonTracksList?.length > 0) {
        // Station A identifiers — from commonTracksList (these are station A tracks)
        this.comparison.commonTracksList.forEach(t => {
          if (t.raw) raws.add(t.raw)
          if (t.title) raws.add(t.title)
        })
        // Station B identifiers — explicit list returned by backend
        if (this.comparison.sharedRawsB?.length) {
          this.comparison.sharedRawsB.forEach(r => { if (r) raws.add(r) })
        } else {
          // Fallback: use rawB stored per item
          this.comparison.commonTracksList.forEach(t => {
            if (t.rawB) raws.add(t.rawB)
            if (t.titleB) raws.add(t.titleB)
          })
        }
        return raws
      }

      // Fallback to exact raw matching for non-fingerprinted stations
      const rawsB = new Set(this.topPlayedB.map(t => t.raw))
      this.topPlayedA.filter(t => rawsB.has(t.raw)).forEach(t => raws.add(t.raw))
      this.topPlayedB.filter(t => {
        const rawsA = new Set(this.topPlayedA.map(a => a.raw))
        return rawsA.has(t.raw)
      }).forEach(t => raws.add(t.raw))
      return raws
    },
    sharedTracks() {
      if (this.comparison?.commonTracksList?.length > 0) {
        // Build a map of topPlayedA by title for lookup
        return this.comparison.commonTracksList.map(item => ({
          ...item,
          countA: item.count || 0,
          countB: item.countB || 0
        })).sort((a, b) => b.countA - a.countA)
      }
      const mapB = Object.fromEntries(this.topPlayedB.map(t => [t.raw, t]))
      return this.topPlayedA
        .filter(t => this.sharedRaws.has(t.raw))
        .map(t => ({ ...t, countA: t.count, countB: mapB[t.raw]?.count || 0 }))
        .sort((a, b) => (b.countA + b.countB) - (a.countA + a.countA))
    },
    overlapPct() {
      const total = new Set([...this.topPlayedA.map(t => t.raw), ...this.topPlayedB.map(t => t.raw)]).size
      if (total === 0) return 0
      const matched = this.comparison?.commonTracksList?.length || this.sharedTracks.length
      return Math.round((matched / total) * 100)
    },
    hasCategoriesA() {
      return this.comparison?.stationA?.typeCategories?.length > 0 &&
        Object.keys(this.comparison?.statsA?.byCategory || {}).length > 0
    },
    hasCategoriesB() {
      return this.comparison?.stationB?.typeCategories?.length > 0 &&
        Object.keys(this.comparison?.statsB?.byCategory || {}).length > 0
    },
    totalPagesA() { return Math.ceil(this.topPlayedA.length / this.perPage) },
    totalPagesB() { return Math.ceil(this.topPlayedB.length / this.perPage) },
    totalPagesShared() { return Math.ceil(this.sharedTracks.length / this.perPage) },
    paginatedA() {
      const s = (this.pageA - 1) * this.perPage
      return this.topPlayedA.slice(s, s + this.perPage)
    },
    paginatedB() {
      const s = (this.pageB - 1) * this.perPage
      return this.topPlayedB.slice(s, s + this.perPage)
    },
    paginatedShared() {
      const s = (this.pageShared - 1) * this.perPage
      return this.sharedTracks.slice(s, s + this.perPage)
    }
  },
  async created() {
    await this.loadStations()
    const { a, b } = this.$route.query
    if (a) this.stationA = a
    if (b) this.stationB = b
    if (this.canCompare) await this.loadComparison()
  },
  methods: {
    async loadStations() {
      try {
        const response = await fetchStations()
        this.stations = response.stations || []
      } catch (err) {
        this.error = 'Failed to load stations'
      }
    },
    async loadComparison() {
      if (!this.canCompare) return
      this.loading = true
      this.error = null
      this.pageA = 1
      this.pageB = 1
      this.pageShared = 1
      try {
        if (this.useCustomDates && this.customFrom && this.customTo) {
          this.comparison = await compareStations(this.stationA, this.stationB, null, this.customFrom, this.customTo)
        } else {
          this.comparison = await compareStations(this.stationA, this.stationB, this.selectedDays)
        }
        this.$router.replace({ query: { a: this.stationA, b: this.stationB } })
        this.$nextTick(() => { this.renderCharts() })
      } catch (err) {
        this.error = 'Failed to load comparison'
        this.comparison = null
      } finally {
        this.loading = false
      }
    },
    selectRange(days) {
      this.selectedDays = days
      this.useCustomDates = false
    },
    onCustomDateChange() {
      if (this.customFrom && this.customTo) this.useCustomDates = true
    },
    isShared(item) {
      return this.sharedRaws.has(item.raw)
    },
    getCategoryColor(category, station = 'A') {
      if (!category) return '#999999'
      const stationData = station === 'A' ? this.comparison?.stationA : this.comparison?.stationB
      const categories = stationData?.typeCategories || []
      const found = categories.find(c => c.label === category)
      if (found?.color) return found.color
      const hash = category.split('').reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0)
      const palette = ['#5D8AA8','#E57373','#81C784','#FFB74D','#64B5F6','#BA68C8','#4DB6AC','#FF8A65','#A1887F','#90A4AE']
      return palette[Math.abs(hash) % palette.length]
    },
    renderCharts() {
      this.renderCategoryChart('A')
      this.renderCategoryChart('B')
    },
    renderCategoryChart(station) {
      const key = `category${station}`
      if (this.charts[key]) { this.charts[key].destroy() }
      const canvas = this.$refs[`categoryChart${station}`]
      if (!canvas) return
      const byCategory = station === 'A'
        ? this.comparison?.statsA?.byCategory || {}
        : this.comparison?.statsB?.byCategory || {}
      const entries = Object.entries(byCategory).sort((a, b) => b[1] - a[1])
      if (entries.length === 0) return
      const labels = entries.map(([cat]) => cat)
      const data = entries.map(([, count]) => count)
      const total = data.reduce((s, v) => s + v, 0)
      this.charts[key] = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{ data, backgroundColor: labels.map(l => this.getCategoryColor(l, station)), borderWidth: 2, borderColor: '#fff' }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right', labels: { font: { size: 10 }, padding: 6, boxWidth: 10, usePointStyle: true } },
            tooltip: { callbacks: { label: ctx => `${ctx.label}: ${ctx.raw.toLocaleString()} (${((ctx.raw/total)*100).toFixed(1)}%)` } }
          }
        }
      })
    },
    exportToCsv() {
      if (!this.comparison) return
      let csv = `"${this.comparison.stationA.name}",,,,,"${this.comparison.stationB.name}",,,\n`
      csv += 'Title,Artist,Count,Rank,,Title,Artist,Count,Rank\n'
      const max = Math.max(this.topPlayedA.length, this.topPlayedB.length)
      for (let i = 0; i < max; i++) {
        const a = this.topPlayedA[i]
        const b = this.topPlayedB[i]
        const esc = v => `"${(v||'').replace(/"/g,'""')}"`
        csv += a ? `${esc(a.title||a.raw)},${esc(a.artist||'')},${a.count},${i+1}` : ',,,'
        csv += ',,'
        csv += b ? `${esc(b.title||b.raw)},${esc(b.artist||'')},${b.count},${i+1}` : ',,,'
        csv += '\n'
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `compare_${this.stationA}_${this.stationB}_${this.today}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
  beforeUnmount() {
    Object.values(this.charts).forEach(c => { if (c) c.destroy() })
  }
}
</script>

<style scoped>
.compare-view {
  min-height: calc(100vh - 74px);
  background: var(--gray-50);
}

.content {
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
}

/* ─── Controls ─── */
.controls-bar {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
  background: var(--white);
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-sm);
}

.station-pickers {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex: 1;
}

.picker-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 140px;
}

.picker-group label {
  font-size: 11px;
  font-weight: 600;
  color: var(--gray-400);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.station-select {
  padding: 9px 32px 9px 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  background: var(--white);
  color: var(--gray-900);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  transition: border-color 0.15s;
}

.station-select:focus {
  outline: none;
  border-color: var(--accent);
}

.vs-badge {
  font-size: 11px;
  font-weight: 700;
  color: var(--gray-400);
  background: var(--gray-100);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-bottom: 2px;
}

.controls-right {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.date-controls {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.range-tabs {
  display: flex;
  background: var(--gray-100);
  border-radius: 6px;
  padding: 3px;
  gap: 2px;
}

.range-tab {
  flex: 1;
  text-align: center;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--gray-500);
  cursor: pointer;
  transition: all 0.15s;
}

.range-tab.active {
  background: var(--white);
  color: var(--gray-900);
  box-shadow: var(--shadow-sm);
}

.range-tab:disabled { opacity: 0.5; cursor: not-allowed; }

.date-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.date-inputs input[type="date"] {
  padding: 5px 8px;
  font-size: 12px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-700);
}

.date-inputs input[type="date"]:focus {
  outline: none;
  border-color: var(--accent);
}

.date-sep {
  font-size: 12px;
  color: var(--gray-400);
}

.action-btns {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.btn-compare {
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  background: var(--accent);
  color: var(--white);
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.btn-compare:hover:not(:disabled) { opacity: 0.9; }
.btn-compare:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-export {
  padding: 9px 12px;
  font-size: 13px;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-export:hover:not(:disabled) { background: var(--gray-50); }
.btn-export:disabled { opacity: 0.4; cursor: not-allowed; }

/* ─── States ─── */
.state-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: var(--white);
  border-radius: 10px;
  color: var(--gray-400);
}

.state-panel svg { width: 36px; height: 36px; margin-bottom: 16px; }
.state-panel p { font-size: 14px; }
.state-panel.error { color: var(--error); }

/* ─── Summary strip ─── */
.summary-strip {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  background: var(--white);
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: var(--shadow-sm);
  align-items: center;
}

.summary-station {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-station:last-child {
  align-items: flex-end;
  text-align: right;
}

.stn-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--gray-900);
  padding: 2px 10px;
  border-radius: 4px;
  display: inline-block;
}

.stn-name.a {
  border-left: 4px solid #5D8AA8;
  padding-left: 10px;
}

.stn-name.b {
  border-right: 4px solid #81C784;
  padding-right: 10px;
}

.stn-metrics {
  display: flex;
  align-items: center;
  gap: 16px;
}

.summary-station:last-child .stn-metrics {
  justify-content: flex-end;
}

.metric {
  display: flex;
  flex-direction: column;
}

.metric-val {
  font-size: 22px;
  font-weight: 700;
  color: var(--gray-900);
  line-height: 1;
}

.metric-lbl {
  font-size: 11px;
  color: var(--gray-400);
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.metric-divider {
  width: 1px;
  height: 32px;
  background: var(--gray-200);
}

.summary-overlap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.overlap-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--gray-100);
  border: 3px solid var(--gray-200);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.overlap-pct {
  font-size: 18px;
  font-weight: 700;
  color: var(--gray-900);
  line-height: 1;
}

.overlap-lbl {
  font-size: 9px;
  color: var(--gray-400);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

.overlap-detail {
  font-size: 11px;
  color: var(--gray-500);
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ─── Main grid ─── */
.main-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.main-grid:has(.shared-col) {
  grid-template-columns: 1fr 0.7fr 1fr;
}

.station-col, .shared-col {
  background: var(--white);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
}

/* ─── Column headers ─── */
.col-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--gray-100);
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.col-header.a { border-top: 3px solid #5D8AA8; }
.col-header.b { border-top: 3px solid #81C784; }
.col-header.shared { border-top: 3px solid var(--gray-300); }

.col-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-900);
}

.col-sub {
  font-size: 11px;
  color: var(--gray-400);
}

/* ─── Chart ─── */
.chart-card {
  padding: 16px;
  border-bottom: 1px solid var(--gray-100);
}

.chart-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 10px;
}

.chart-wrap {
  height: 140px;
}

/* ─── Track list ─── */
.track-list { flex: 1; }

.track-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-100);
}

.th-rank { width: 24px; font-size: 10px; font-weight: 600; color: var(--gray-400); text-align: center; flex-shrink: 0; }
.th-title { flex: 1; font-size: 10px; font-weight: 600; color: var(--gray-400); text-transform: uppercase; letter-spacing: 0.3px; }
.th-count { font-size: 10px; font-weight: 600; color: var(--gray-400); text-transform: uppercase; letter-spacing: 0.3px; min-width: 40px; text-align: right; }

.track-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  border-bottom: 1px solid var(--gray-50);
  transition: background 0.1s;
}

.track-row:hover { background: var(--gray-50); }
.track-row:last-child { border-bottom: none; }

.track-row.shared { background: rgba(93, 138, 168, 0.04); }
.track-row.shared:hover { background: rgba(93, 138, 168, 0.08); }
.shared-row { background: rgba(93, 138, 168, 0.03); }

.tr-rank { width: 24px; font-size: 11px; font-weight: 600; color: var(--gray-300); text-align: center; flex-shrink: 0; }

.tr-info { flex: 1; min-width: 0; }

.tr-title {
  font-size: 12px;
  font-weight: 500;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.tr-artist {
  font-size: 11px;
  color: var(--gray-400);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.tr-badge {
  font-size: 9px;
  font-weight: 600;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.tr-shared-dot {
  font-size: 8px;
  color: #5D8AA8;
  flex-shrink: 0;
}

.tr-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-600);
  min-width: 40px;
  text-align: right;
  flex-shrink: 0;
}

.no-data {
  padding: 40px 20px;
  text-align: center;
  color: var(--gray-400);
  font-size: 13px;
}

/* ─── Pagination ─── */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px;
  border-top: 1px solid var(--gray-100);
}

.pg-btn {
  width: 28px;
  height: 28px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.15s;
}

.pg-btn:hover:not(:disabled) { background: var(--gray-50); }
.pg-btn:disabled { opacity: 0.3; cursor: not-allowed; }

.pg-info { font-size: 12px; color: var(--gray-400); min-width: 50px; text-align: center; }

/* ─── Responsive ─── */
@media (max-width: 1000px) {
  .main-grid, .main-grid:has(.shared-col) {
    grid-template-columns: 1fr;
  }
  .summary-strip {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .summary-station:last-child { align-items: flex-start; text-align: left; }
  .stn-name.b { border-right: none; border-left: 4px solid #81C784; }
  .controls-bar { flex-direction: column; align-items: stretch; }
  .station-pickers { flex-direction: column; }
}
</style>
